import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, TrendingUp } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, Legend } from "recharts";
import { api, fmtDay } from "../lib/api";

const FULL = { Mon: "Mondays", Tue: "Tuesdays", Wed: "Wednesdays", Thu: "Thursdays", Fri: "Fridays", Sat: "Saturdays", Sun: "Sundays" };

function Tips({ d }) {
  const names = d.busiest.map((x) => FULL[x]).join(" and ");
  return (
    <div className="rounded-xl bg-[#FFF4F1] border border-[#FFD9CF] p-4 text-sm text-slate-700 space-y-2" data-testid="demand-tips">
      <p className="flex items-center gap-2 font-bold text-slate-900"><Flame size={16} className="text-[#FF8267]" />{names} are your busiest days</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Consider a small price rise or a weekend/peak rate for {names} on your <Link to="/provider/adverts" className="underline font-medium">service adverts</Link>.</li>
        <li>{d.limit ? `Your limit is ${d.limit} bookings a day. If you can, raise it on busy weeks` : "Set a daily booking limit, or keep it open"} in <Link to="/provider/profile" className="underline font-medium">Profile → Availability</Link> so you don't turn work away.</li>
        <li>Quieter days could be good for a featured advert or a small discount to even out your week.</li>
      </ul>
    </div>
  );
}

export default function DemandInsights() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/provider/demand").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d) return null;
  const empty = !d.total_bookings && !d.total_requests;
  return (
    <section className="card p-5 mt-6 space-y-4" data-testid="demand-insights">
      <div><h2 className="font-bold text-lg flex items-center gap-2"><TrendingUp size={18} className="text-[#FF8267]" />Demand by day</h2>
        <p className="text-xs text-slate-500">Bookings and service requests over the last 120 days, by day of the week.</p></div>
      {empty ? <p className="text-sm text-slate-600" data-testid="demand-empty">No bookings or requests yet. Once homeowners start booking, your busiest days will show here.</p> : <>
        <div className="h-64"><ResponsiveContainer width="100%" height="100%">
          <BarChart data={d.weekdays} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#EEF0F2" /><XAxis dataKey="day" tick={{ fontSize: 12, fill: "#64748B" }} /><YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
            <Tooltip cursor={{ fill: "#FAF7EE" }} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="bookings" name="Bookings" stackId="a" fill="#374B5C" maxBarSize={36}>{d.weekdays.map((w) => <Cell key={w.day} fill={d.busiest.includes(w.day) ? "#FF8267" : "#374B5C"} />)}</Bar>
            <Bar dataKey="requests" name="Requests" stackId="a" fill="#CBD5E1" radius={[4, 4, 0, 0]} maxBarSize={36} />
          </BarChart>
        </ResponsiveContainer></div>
        {d.busiest.length > 0 ? <Tips d={d} /> : <p className="text-sm text-slate-600" data-testid="demand-even">Your demand is spread fairly evenly across the week so far.</p>}
      </>}
      {d.upcoming.length > 0 && <div data-testid="demand-upcoming"><h3 className="font-semibold text-sm text-slate-900 mb-2">Busiest upcoming dates (next 30 days)</h3>
        <div className="flex flex-wrap gap-2">{d.upcoming.map((u) => <span key={u.date} className={`chip ${u.full ? "bg-[#FFF1EE] text-[#D9583D]" : "bg-slate-100 text-slate-700"}`} data-testid={`demand-date-${u.date}`}>{fmtDay(u.date)} · {u.bookings} booking{u.bookings === 1 ? "" : "s"}{u.full ? " · full" : ""}</span>)}</div></div>}
    </section>
  );
}
